<div class="min-h-screen flex flex-col font-sans bg-cloud-mist overflow-x-hidden">

  <!-- ── Hero ───────────────────────────────────────────────────────────── -->
  <section
    class="relative flex-1 flex items-center bg-gradient-to-br from-[#071e38] via-midnight-indigo to-[#0d2d4e]">
    <div
      class="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:14px_24px]"
      aria-hidden="true"></div>
    <div
      class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_15%_0%,rgba(0,107,255,0.25),transparent_50%),radial-gradient(ellipse_60%_50%_at_95%_30%,rgba(130,71,245,0.20),transparent_55%)]"
      aria-hidden="true"></div>

    <div class="tfr-page-shell relative py-12 sm:py-16 lg:py-28">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

        <div class="flex flex-col items-start">
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide
                   bg-white/10 text-snow-white ring-1 ring-white/20 mb-5 sm:mb-8">
            Para propietarios de espacios · España
          </span>
          <h1 class="text-[36px] sm:text-[48px] lg:text-[50px] font-bold leading-[1.1] tracking-tight text-snow-white mb-4 sm:mb-6">
            Tu espacio vacío<br />
            <span class="text-action-blue">ya está costándote dinero.</span>
          </h1>
          <p class="text-[16px] sm:text-[18px] leading-[1.65] text-snow-white/70 max-w-md mb-7 sm:mb-10">
            TimeFlexRent convierte las horas muertas de tu local, sala, nave o estudio en ingresos reales — con el calendario, el cobro y el contrato totalmente automatizados.
          </p>
          <div class="flex flex-wrap items-center gap-3 mb-4">
            <button
              type="button"
              (click)="goToCta()"
              class="inline-flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-xl text-[14px] sm:text-[15px] font-semibold
                     text-white bg-action-blue shadow-[0_8px_24px_rgba(0,107,255,0.28)]
                     transition-all duration-200 hover:brightness-110 hover:-translate-y-0.5
                     focus:outline-none focus:ring-2 focus:ring-action-blue/40">
              Publica tu espacio gratis ahora
              <lucide-icon name="arrow-right" [size]="18" />
            </button>
          </div>
          <p class="text-[13px] text-snow-white/50">
            Registro gratuito en 3 minutos. Sin tarjeta. Sin compromisos.
          </p>
        </div>

        <div class="hidden lg:flex justify-center lg:justify-end relative">
          <div class="absolute inset-0 -z-0 flex items-center justify-center pointer-events-none">
            <div class="w-80 h-80 rounded-full blur-3xl opacity-60 bg-gradient-to-br from-action-blue/40 to-royal-amethyst/30"></div>
          </div>
          <div class="relative z-10 drop-shadow-[0_24px_48px_rgba(0,0,0,0.35)]">
            <app-hero-card />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Espacios en plataforma (prueba social para propietarios) ───────── -->
  @if (!spacesLoading() && latestSpaces().length > 0) {
    <section class="bg-cloud-mist py-20 border-b border-pale-gray/60">
      <div class="tfr-page-shell">
        <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <h2 class="text-[28px] font-bold leading-[1.2] text-midnight-indigo mb-2">
              Tu espacio, visto por miles de clientes
            </h2>
            <p class="text-[15px] leading-[1.6] text-slate-blue max-w-lg">
              Así aparecen los espacios publicados en TimeFlexRent. El tuyo podría ser el próximo.
            </p>
          </div>
          <button
            type="button"
            (click)="goToCta()"
            class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-action-blue
                   hover:text-glacier-blue transition-colors shrink-0">
            Publicar mi espacio
            <lucide-icon name="arrow-right" [size]="16" />
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (space of latestSpaces(); track space.id) {
            <a
              [routerLink]="spaceLink(space)"
              class="group flex flex-col bg-white rounded-2xl border border-pale-gray overflow-hidden
                     shadow-tfr-sm transition-all duration-300
                     hover:-translate-y-1 hover:border-action-blue/40 hover:shadow-[0_12px_32px_rgba(0,107,255,0.12)]">
              <div class="relative h-44 overflow-hidden bg-pale-gray">
                @if (coverThumb(space)) {
                  <img
                    [ngSrc]="coverThumb(space)"
                    [alt]="space.name"
                    fill
                    class="object-cover transition-transform duration-500 group-hover:scale-105"
                    [sizes]="cardImageSizes" />
                } @else {
                  <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-pale-gray to-cloud-mist">
                    <lucide-icon name="building-2" [size]="40" class="text-steel-gray" />
                  </div>
                }
                <span
                  class="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide
                         bg-white text-midnight-indigo shadow-tfr-sm ring-1 ring-pale-gray/80">
                  {{ spaceCategoryLabel(space) }}
                </span>
              </div>
              <div class="flex flex-col flex-1 p-5">
                <h3
                  class="text-[15px] font-semibold tracking-tight leading-snug text-midnight-indigo mb-1 truncate"
                  [attr.title]="space.name">
                  {{ space.name }}
                </h3>
                <p class="text-xs text-slate-blue flex items-center gap-1 mb-4 min-w-0">
                  <lucide-icon name="map-pin" [size]="12" class="shrink-0 text-steel-gray" />
                  <span class="truncate">{{ spaceLocationLabel(space) }}</span>
                </p>
                <div class="mt-auto flex items-center justify-between gap-2">
                  <div class="min-w-0">
                    @if (hourlyRate(space) > 0) {
                      <span class="text-midnight-indigo font-bold text-base tabular-nums">{{ hourlyRate(space) }}&euro;</span>
                      <span class="text-steel-gray text-xs ml-1">/ hora</span>
                    } @else {
                      <span class="text-[13px] font-medium text-slate-blue">Consultar tarifa</span>
                    }
                  </div>
                  <span class="text-[11px] font-semibold text-action-blue tracking-wide shrink-0 group-hover:underline">
                    Ver espacio
                  </span>
                </div>
              </div>
            </a>
          }
        </div>

        <div class="flex justify-center mt-8 sm:hidden">
          <button
            type="button"
            (click)="goToCta()"
            class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-action-blue">
            Publicar mi espacio
            <lucide-icon name="arrow-right" [size]="16" />
          </button>
        </div>
      </div>
    </section>
  }

  <!-- ── Beneficios para el propietario ────────────────────────────────── -->
  <section class="bg-cloud-mist py-16 lg:py-24 border-b border-pale-gray">
    <div class="tfr-page-shell">
      <div class="text-center mb-14 max-w-2xl mx-auto">
        <h2 class="text-[28px] sm:text-[32px] font-bold leading-[1.2] text-midnight-indigo mb-3">
          Todo lo que necesitas, nada que no uses
        </h2>
        <p class="text-[16px] leading-[1.65] text-slate-blue">
          Cada funcionalidad está diseñada para resolver un problema real de los propietarios.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        @for (b of ownerBenefits(); track b.title; let i = $index) {
          <article
            class="flex flex-col rounded-2xl bg-white border border-pale-gray p-8
                   shadow-tfr-sm transition-all duration-300 hover:-translate-y-1">
            <div
              class="h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center mb-6"
              [class.bg-action-blue]="i === 0"
              [class.bg-midnight-indigo]="i === 1"
              [class.bg-royal-amethyst]="i === 2">
              <lucide-icon [name]="b.icon" [size]="22" class="text-white" />
            </div>
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-5 w-fit"
              [class.bg-action-blue/10]="i === 0"
              [class.text-action-blue]="i === 0"
              [class.bg-midnight-indigo/10]="i === 1"
              [class.text-midnight-indigo]="i === 1"
              [class.bg-royal-amethyst/10]="i === 2"
              [class.text-royal-amethyst]="i === 2">
              {{ b.tag }}
            </span>
            <h3 class="text-[18px] font-bold text-midnight-indigo mb-3 leading-snug">{{ b.title }}</h3>
            <p class="text-[14px] leading-[1.7] text-slate-blue">{{ b.desc }}</p>
          </article>
        }
      </div>
    </div>
  </section>

  <!-- ── FAQ / Transparencia ────────────────────────────────────────────── -->
  <section class="bg-white py-16 lg:py-20 border-b border-pale-gray">
    <div class="tfr-page-shell-narrow">
      <div class="text-center mb-12">
        <h2 class="text-[28px] sm:text-[32px] font-bold leading-[1.2] text-midnight-indigo mb-3">
          Sin letra pequeña
        </h2>
        <p class="text-[16px] leading-[1.65] text-slate-blue">
          Las dudas que siempre surgen, respondidas de forma categórica.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        @for (faq of ownerFaqs(); track faq.q) {
          <div class="rounded-2xl border border-pale-gray bg-cloud-mist p-7">
            <p class="text-[16px] font-semibold text-midnight-indigo mb-2">{{ faq.q }}</p>
            <p class="text-[14px] leading-[1.7] text-slate-blue">{{ faq.a }}</p>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- ── Testimonios de propietarios ───────────────────────────────────── -->
  <section class="bg-cloud-mist/60 py-20 border-b border-pale-gray">
    <div class="tfr-page-shell">
      <div class="text-center mb-10">
        <h2 class="text-[24px] font-bold text-midnight-indigo mb-2">Testimonios de propietarios</h2>
        <p class="text-[14px] text-slate-blue max-w-md mx-auto">
          Quienes ya gestionan sus espacios con TimeFlexRent.
        </p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        @for (t of testimonials(); track t.name) {
          <blockquote
            class="rounded-2xl border border-pale-gray bg-white p-6 shadow-tfr-sm">
            <p class="text-[14px] leading-[1.65] text-slate-blue mb-5">&ldquo;{{ t.quote }}&rdquo;</p>
            <footer class="flex items-center gap-3">
              <div
                class="h-10 w-10 rounded-full flex items-center justify-center text-[12px] font-bold
                       bg-action-blue/10 text-action-blue ring-2 ring-white shadow-tfr-sm">
                {{ t.initials }}
              </div>
              <div>
                <p class="text-[13px] font-semibold text-midnight-indigo">{{ t.name }}</p>
                <p class="text-[11px] text-steel-gray">{{ t.role }}</p>
              </div>
            </footer>
          </blockquote>
        }
      </div>
    </div>
  </section>

  <!-- ── Cierre propietario ──────────────────────────────────────────────── -->
  <section class="relative overflow-hidden py-16 lg:py-20 border-b border-action-blue/20">
    <div
      class="absolute inset-0 bg-gradient-to-br from-slate-900 via-midnight-indigo to-slate-800"
      aria-hidden="true"></div>
    <div
      class="pointer-events-none absolute inset-0 opacity-[0.07]"
      style="background-image: repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 48px);"
      aria-hidden="true"></div>
    <div
      class="pointer-events-none absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-action-blue/15 to-transparent"
      aria-hidden="true"></div>

    <div class="tfr-page-shell relative text-center">
      <div class="mx-auto max-w-2xl">
        <h2 class="text-[26px] sm:text-[30px] font-bold leading-[1.2] text-white mb-4">
          Cada hora vacía es dinero que no vuelve.
        </h2>
        <p class="text-[15px] leading-[1.75] text-steel-gray mb-8 max-w-lg mx-auto">
          TimeFlexRent no te pide ninguna inversión inicial, ninguna cuota mensual y ningún riesgo. Solo cinco minutos para publicar tu espacio. A partir de ahí, la plataforma trabaja por ti.
        </p>
        <button
          type="button"
          (click)="goToCta()"
          class="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-[15px] font-bold
                 text-midnight-indigo bg-white shadow-tfr-md ring-1 ring-pale-gray/60
                 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-tfr-lg
                 focus:outline-none focus:ring-2 focus:ring-action-blue/40">
          Registra tu espacio ahora — es gratis
          <lucide-icon name="arrow-right" [size]="18" />
        </button>
        <p class="mt-4 text-[12px] text-steel-gray/70">
          Únete a los propietarios que ya convierten horas muertas en ingresos reales.
        </p>
      </div>
    </div>
  </section>

  <app-site-footer />
</div>
